/**
 * Where a strategy object lives (an Objective, a Goal; ../../supabase/functions/
 * _shared/syncedObjects.ts lists them). For an Objective: the other expressions
 * of it in Priorities.ai (a
 * V2MOM's line, an OKR, a Goal, a Desired Outcome brief — each points at the one
 * objective, canon 01 › Strategic Objectives) and the records in other systems
 * it is linked to and kept in step with (a Linear initiative, a Jira issue;
 * migration 1049, supabase/functions/_shared/syncedObjects.ts).
 *
 * A link is an entity_external_ids row, made through the same registerExternalId
 * an Item's link uses. Its name and description then cross under the connector's
 * Field Sync Rules: changes here are queued by the database and sent by
 * `dispatch`; changes there come in by the `webhook` and `apply` functions.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { deactivateExternalId, getExternalIds, notifyExternalIdsChanged, registerExternalId } from './connectors';
import { triggerDispatch } from './syncEngine';
import { SYNCED_OBJECTS, type SyncedObjectType } from '../../supabase/functions/_shared/syncedObjects.ts';

export interface ObjectiveExpression {
  kind: 'v2mom' | 'okr' | 'goal' | 'desired_outcome';
  id: string;
  title: string;
  /** Where it opens. */
  href: string;
}

const KIND_ORDER: ObjectiveExpression['kind'][] = ['v2mom', 'okr', 'goal', 'desired_outcome'];

/** The other expressions of one objective in this workspace. */
export async function objectiveExpressions(workspaceId: string, objectiveId: string): Promise<ObjectiveExpression[]> {
  const db = getTypedSupabaseClient();
  const [v2mom, okrs, goals, briefs] = await Promise.all([
    db.from('v2mom_entries').select('id, title, v2mom_id').eq('workspace_id', workspaceId).eq('objective_id', objectiveId),
    db.from('okrs').select('id, title').eq('workspace_id', workspaceId).eq('strategic_objective_id', objectiveId),
    db.from('goals').select('id, name').eq('workspace_id', workspaceId).eq('objective_id', objectiveId),
    db.from('desired_outcome_briefs').select('id, title').eq('workspace_id', workspaceId).eq('objective_id', objectiveId),
  ]);
  const out: ObjectiveExpression[] = [
    ...(v2mom.data ?? []).map((r) => ({ kind: 'v2mom' as const, id: r.id, title: r.title ?? '', href: `/prioritization/strategy/v2mom/${r.v2mom_id}` })),
    ...(okrs.data ?? []).map((r) => ({ kind: 'okr' as const, id: r.id, title: r.title ?? '', href: '/prioritization/strategy/okrs' })),
    ...(goals.data ?? []).map((r) => ({ kind: 'goal' as const, id: r.id, title: r.name ?? '', href: `/prioritization/strategy/goals?objectiveId=${objectiveId}` })),
    ...(briefs.data ?? []).map((r) => ({ kind: 'desired_outcome' as const, id: r.id, title: r.title ?? '', href: `/prioritization/desired-outcomes/${r.id}` })),
  ];
  return out.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
}

export const EXPRESSION_LABELS: Record<ObjectiveExpression['kind'], string> = {
  v2mom: 'V2MOM line',
  okr: 'OKR',
  goal: 'Goal',
  desired_outcome: 'Desired Outcome',
};

/** The systems a strategy object can be linked to, and what each calls the record. */
export function linkSystemsFor(type: SyncedObjectType) {
  return Object.entries(SYNCED_OBJECTS[type].systems).map(([connectorType, sys]) => ({
    connectorType,
    recordLabel: sys.recordLabel,
    fields: sys.fields,
  }));
}

/**
 * Link a strategy object to a record there. `theirValues` is what the record says
 * now, by our field (name, description): kept on the link so a value that differs
 * is shown as a difference and an echo is not sent back.
 */
export async function linkStrategyObject(params: {
  workspaceId: string;
  type: SyncedObjectType;
  id: string;
  connectorType: string;
  externalId: string;
  externalUrl?: string;
  externalLabel?: string;
  theirValues: Record<string, unknown>;
}): Promise<{ error: string | null }> {
  const saved = await registerExternalId({
    workspaceId: params.workspaceId,
    entityType: params.type,
    internalId: params.id,
    connectorType: params.connectorType,
    externalId: params.externalId,
    externalUrl: params.externalUrl,
    externalLabel: params.externalLabel,
    externalValues: params.theirValues,
  });
  if (!saved.error) notifyExternalIdsChanged(params.type, params.id);
  return saved;
}

export async function unlinkStrategyObject(params: {
  workspaceId: string;
  type: SyncedObjectType;
  id: string;
  connectorType: string;
  externalId: string;
}): Promise<{ error: string | null }> {
  const r = await deactivateExternalId({
    workspaceId: params.workspaceId,
    entityType: params.type,
    internalId: params.id,
    connectorType: params.connectorType,
    externalId: params.externalId,
  });
  if (!r.error) notifyExternalIdsChanged(params.type, params.id);
  return r;
}

/** After a strategy object is saved: if it is linked anywhere, send what changed now rather than at the next 15-minute run. */
export async function sendStrategyObjectChanges(workspaceId: string, type: SyncedObjectType, id: string): Promise<void> {
  const links = await getExternalIds(workspaceId, type, id);
  if (links.length === 0) return;
  await triggerDispatch({ workspaceId, mode: 'pending' });
}
